// Stealth-Agent FE — Hourly Insights. Hour-by-hour breakdown for a device/day:
// instant per-hour stats from /hourly-insights/slots, plus an on-demand Claude
// narrative per hour from /hourly-insights/stream. Timezone is DYNAMIC (viewer's
// browser tz). Premium SaaS UI + framer-motion. ISOLATED: no TG imports.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, DatePicker, Empty, Button } from 'antd';
import { Clock, Sunrise, Sun, Sunset, Moon, Zap, Keyboard, MousePointerClick, Repeat, Sparkles, Download, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import { getDevices, getHourlySlots, HOURLY_INSIGHTS_STREAM } from '../api/client';
import { useStealthAiStream, mdToHtml } from './useStealthAiStream';
import { fmtDuration, isOnline, relativeTime, deviceDisplayName, deviceSelectLabel } from './utils';
import { Card, SectionTitle, Skeleton, Bar, EASE, StatusPill, downloadReportPDF } from './ui';

// Viewer's browser timezone (e.g. "America/New_York", "Asia/Kolkata").
const VIEWER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'; }
  catch { return 'Asia/Kolkata'; }
})();

const partIcon = (p) => {
  if (p === 'Morning') return <Sunrise size={14} />;
  if (p === 'Afternoon') return <Sun size={14} />;
  if (p === 'Evening') return <Sunset size={14} />;
  return <Moon size={14} />;
};
const partTint = (p) => ({
  Morning: 'text-amber-500', Afternoon: 'text-sky-500', Evening: 'text-violet-500', Night: 'text-indigo-400',
}[p] || 'text-gray-400');

export default function HourlyInsights() {
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(dayjs());
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [activeHour, setActiveHour] = useState(null);
  const ai = useStealthAiStream(HOURLY_INSIGHTS_STREAM);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const d = await getDevices();
      setDevices(d.devices || []);
      setSelected((cur) => cur || (d.devices && d.devices[0] && d.devices[0].device_id) || null);
    } catch { /* noop */ } finally { setDevicesLoading(false); }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  async function loadSlots() {
    if (!selected) return;
    setLoading(true); setActiveHour(null); ai.reset();
    try {
      const d = await getHourlySlots({ device_id: selected, date: date.format('YYYY-MM-DD'), tz: VIEWER_TZ });
      setSlots(d.slots || []);
    } catch { setSlots([]); } finally { setLoading(false); }
  }
  useEffect(() => { if (selected) loadSlots(); /* eslint-disable-next-line */ }, [selected, date]);

  async function refreshAll() {
    await fetchDevices();
    if (selected) await loadSlots();
  }

  const explainHour = (hour) => { setActiveHour(hour); ai.reset(); ai.run({ device_id: selected, date: date.format('YYYY-MM-DD'), hour, tz: VIEWER_TZ }); };

  const deviceOptions = devices.map((d) => ({
    value: d.device_id,
    label: deviceSelectLabel(d),
  }));
  const dev = devices.find((d) => d.device_id === selected);

  const dayTotals = useMemo(() => {
    let active = 0, total = 0;
    slots.forEach((s) => { active += s.active_sec || 0; total += s.total_sec || 0; });
    return { active, total, pct: total ? Math.round((active / total) * 100) : 0 };
  }, [slots]);

  const sel = slots.find((s) => s.hour === activeHour);

  return (
    <div className="space-y-5">
      {/* Controls */}
      <Card className="p-3.5 flex items-center gap-3 flex-wrap">
        <Select showSearch optionFilterProp="label" style={{ minWidth: 320 }}
          placeholder="Select device" value={selected} onChange={setSelected} options={deviceOptions} />
        <DatePicker value={date} onChange={(d) => setDate(d || dayjs())} allowClear={false} disabledDate={(d) => d && d > dayjs().endOf('day')} />
        <span className="text-xs text-gray-500 flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-50 dark:bg-white/[0.04]"><Clock size={12} /> {VIEWER_TZ}</span>
        <Button icon={<RefreshCw size={14} />} onClick={refreshAll} loading={devicesLoading || loading}>Refresh</Button>
        {dev && <StatusPill online={isOnline(dev)} lastSeen={relativeTime(dev.last_heartbeat_at || dev.last_seen_at)} interval={dev.report_interval} />}
        {slots.length > 0 && (
          <span className="text-xs text-gray-400 ml-auto tabular-nums">Worked {fmtDuration(dayTotals.total)} · {dayTotals.pct}% active</span>
        )}
      </Card>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-2 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          <div className="lg:col-span-3"><Skeleton className="h-72 w-full" /></div>
        </div>
      ) : slots.length === 0 ? (
        <Card className="p-10"><Empty description="No activity for this device on this day." /></Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Hour rail */}
          <div className="lg:col-span-2 space-y-2.5">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 px-1">Tracked hours · {date.format('ddd, MMM D')}</div>
            {slots.map((s, i) => (
              <motion.button
                key={s.hour}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04, ease: EASE }}
                whileHover={{ x: 2 }} onClick={() => explainHour(s.hour)}
                className={`w-full text-left rounded-2xl border p-4 transition-shadow ${
                  activeHour === s.hour
                    ? 'border-indigo-400 dark:border-indigo-500/50 bg-indigo-50/60 dark:bg-indigo-500/[0.08] shadow-[0_4px_20px_rgba(99,102,241,0.15)]'
                    : 'border-gray-200/80 dark:border-white/[0.06] bg-white dark:bg-[#0f1115] hover:border-indigo-200 dark:hover:border-indigo-500/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
                    <span className={partTint(s.part_of_day)}>{partIcon(s.part_of_day)}</span> {s.label}
                  </div>
                  <span className="text-[11px] text-gray-400">{s.part_of_day}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Bar pct={s.active_pct} />
                  <span className="text-[11px] text-gray-500 tabular-nums w-9 text-right">{s.active_pct}%</span>
                </div>
                <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
                  <span className="font-medium text-gray-600 dark:text-gray-300">{s.active_label}</span>
                  <span className="flex items-center gap-1"><Keyboard size={11} />{s.keys}</span>
                  <span className="flex items-center gap-1"><MousePointerClick size={11} />{s.clicks}</span>
                  <span className="flex items-center gap-1"><Repeat size={11} />{s.switches}</span>
                </div>
                {s.top_apps && s.top_apps.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.top_apps.slice(0, 3).map((a) => (
                      <span key={a.name} className="text-[10px] px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-white/[0.05] text-gray-500">{a.name} · {a.label}</span>
                    ))}
                  </div>
                )}
              </motion.button>
            ))}
          </div>

          {/* Selected hour detail + AI narrative */}
          <div className="lg:col-span-3">
            <Card className="p-6 min-h-[300px] sticky top-4 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-gradient-to-br from-indigo-500/10 to-violet-500/10 blur-3xl pointer-events-none" />
              {activeHour == null ? (
                <div className="h-[260px] flex flex-col items-center justify-center text-center text-gray-400 dark:text-gray-500">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center mb-3"><Zap className="w-5 h-5 text-indigo-500" /></div>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Pick an hour to analyze</div>
                  <div className="text-xs mt-1">AI will explain what was worked on, from the work activity in this hour.</div>
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  <motion.div key={activeHour} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ ease: EASE }}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-semibold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
                        <span className={sel && partTint(sel.part_of_day)}>{sel && partIcon(sel.part_of_day)}</span>
                        {sel ? sel.label : ''}
                      </div>
                      {sel && <div className="text-[11px] text-gray-400 tabular-nums">{sel.reports} records · {sel.ocr_reports} with context</div>}
                    </div>
                    {sel && (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5">
                        <Mini label="Active" v={sel.active_label} />
                        <Mini label="Idle" v={sel.idle_label} />
                        <Mini label="Keys" v={sel.keys} />
                        <Mini label="Clicks" v={sel.clicks} />
                        <Mini label="Switches" v={sel.switches} />
                      </div>
                    )}
                    <SectionTitle icon={Sparkles} right={
                      ai.text && !ai.streaming ? (
                        <Button size="small" icon={<Download size={13} />} onClick={() => downloadReportPDF({
                          title: 'Hourly Insight',
                          subtitle: `${dev ? deviceDisplayName(dev) : 'Device'} · ${sel ? sel.label : ''} · ${date.format('MMMM D, YYYY')}`,
                          bodyHtml: mdToHtml(ai.text),
                          meta: { Date: date.format('YYYY-MM-DD'), Hour: sel ? sel.label : '', Timezone: VIEWER_TZ, Active: sel ? sel.active_label : '' },
                        })}>PDF</Button>
                      ) : null
                    }>
                      <span className="bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">What happened this hour</span>
                    </SectionTitle>
                    {ai.streaming && !ai.text && (
                      <div className="space-y-2.5">
                        <Skeleton className="h-3.5 w-3/4" /><Skeleton className="h-3.5 w-full" /><Skeleton className="h-3.5 w-5/6" />
                        <div className="flex items-center gap-2 text-xs text-indigo-500 pt-1"><span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" /> Analyzing this hour…</div>
                      </div>
                    )}
                    {ai.error && <div className="text-sm text-amber-600">{ai.error}</div>}
                    {ai.text && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                        className="text-[14px] text-gray-700 dark:text-gray-200 leading-[1.7]" dangerouslySetInnerHTML={{ __html: mdToHtml(ai.text) }} />
                    )}
                    {ai.streaming && ai.text && (
                      <motion.span className="inline-block w-1.5 h-4 ml-0.5 align-middle bg-indigo-500 rounded-sm" animate={{ opacity: [1, 0.2, 1] }} transition={{ repeat: Infinity, duration: 1 }} />
                    )}
                  </motion.div>
                </AnimatePresence>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

const Mini = ({ label, v }) => (
  <div className="bg-gray-50 dark:bg-white/[0.03] rounded-xl p-2.5 text-center">
    <div className="font-semibold text-gray-900 dark:text-white text-sm tabular-nums">{v}</div>
    <div className="text-[10px] text-gray-400 mt-0.5">{label}</div>
  </div>
);
